// Workout Tracking Module

import { storage } from './storage.js';
import { dataManager } from './data.js';
import { ui } from './ui.js';

class WorkoutManager {
  constructor() {
    this.initialized = false;
  }

  init() {
    if (this.initialized) return;

    // Listen for data loaded event
    window.addEventListener('dataloaded', () => this.updateTraining());
    window.addEventListener('datachanged', () => this.updateTraining());

    // Setup week and day selectors
    const weekSelect = document.getElementById('week');
    const daySelect = document.getElementById('day');

    if (weekSelect) {
      weekSelect.addEventListener('change', () => this.updateTraining());
    }
    if (daySelect) {
      daySelect.addEventListener('change', () => this.updateTraining());
    }

    // Make functions globally available for onclick handlers
    window.toggleCompletion = (week, day, index, element) => {
      this.toggleCompletion(week, day, index, element);
    };
    window.saveNote = (week, day, index, value) => {
      this.saveNote(week, day, index, value);
    };
    window.saveRating = (week, day, index, rating, element) => {
      this.saveRating(week, day, index, rating, element);
    };

    this.initialized = true;
  }

  getCompletionKey(week, day, exerciseIndex) {
    // Extract day without parentheses (e.g., "LUNEDÌ (A)" -> "LUNEDÌ")
    const dayShort = day.split(' ')[0];
    return `workout-completion-${week}-${dayShort}-${exerciseIndex}`;
  }

  getNoteKey(week, day, exerciseIndex) {
    // Extract day without parentheses (e.g., "LUNEDÌ (A)" -> "LUNEDÌ")
    const dayShort = day.split(' ')[0];
    return `workout-note-${week}-${dayShort}-${exerciseIndex}`;
  }

  getRatingKey(week, day, exerciseIndex) {
    // Extract day without parentheses (e.g., "LUNEDÌ (A)" -> "LUNEDÌ")
    const dayShort = day.split(' ')[0];
    return `workout-rating-${week}-${dayShort}-${exerciseIndex}`;
  }

  isExerciseCompleted(week, day, exerciseIndex) {
    const key = this.getCompletionKey(week, day, exerciseIndex);
    // storage.getItem returns boolean true (from JSON.parse), not string 'true'
    return storage.getItem(key) === true;
  }

  toggleCompletion(week, day, exerciseIndex, element) {
    const key = this.getCompletionKey(week, day, exerciseIndex);
    const dayShort = day.split(' ')[0];
    const dateKey = `workout-completed-${week}-${dayShort}`;
    const isCompleted = !this.isExerciseCompleted(week, day, exerciseIndex);

    storage.setItem(key, isCompleted.toString());

    if (isCompleted) {
      storage.setItem(dateKey, new Date().toISOString().split('T')[0]);
      storage.setItem(`${key}-date`, new Date().toISOString().split('T')[0]);
      element.classList.add('checked');
      element.closest('.exercise-card').classList.add('completed');
      this.showBadge('✓');
    } else {
      storage.removeItem(dateKey);
      storage.removeItem(`${key}-date`);
      element.classList.remove('checked');
      element.closest('.exercise-card').classList.remove('completed');
    }

    // Update progress bar with consistent day format
    this.updateWorkoutProgress(week, dayShort);
    window.dispatchEvent(new Event('workoutcompleted'));
  }

  updateWorkoutProgress(week, day) {
    const data = dataManager.getData();
    if (!data || !data.training || !data.training[week]) {
      return;
    }

    let dayKey = null;
    for (const key of Object.keys(data.training[week])) {
      if (key.startsWith(day)) {
        dayKey = key;
        break;
      }
    }

    if (!dayKey) {
      return;
    }

    const exercises = data.training[week][dayKey];
    let completed = 0;

    exercises.forEach((ex, index) => {
      const isCompleted = this.isExerciseCompleted(week, day, index);
      if (isCompleted) {
        completed++;
      }
    });

    const total = exercises.length;

    const progressContainer = document.getElementById('workoutProgress');
    const progressBar = document.querySelector('.workout-progress-bar');
    const progressText = document.querySelector('.workout-progress-text');

    if (progressContainer && progressBar && progressText) {
      progressContainer.style.display = 'block';
      const percentage = (completed / total) * 100;
      progressBar.style.width = `${percentage}%`;
      progressText.textContent = `${completed}/${total} esercizi completati`;

      if (completed === total && total > 0) {
        this.triggerConfetti();
      }
    }
  }

  saveNote(week, day, exerciseIndex, value) {
    const key = this.getNoteKey(week, day, exerciseIndex);
    storage.setItem(key, value);

    // Show saved indicator
    const textarea = event.target;
    const noteContainer = textarea.closest('.personal-notes');
    const savedIndicator = noteContainer.querySelector('.notes-saved');

    if (savedIndicator) {
      savedIndicator.style.opacity = '1';
      setTimeout(() => {
        savedIndicator.style.opacity = '0';
      }, 2000);
    }
  }

  loadNote(week, day, exerciseIndex) {
    const key = this.getNoteKey(week, day, exerciseIndex);
    return storage.getItem(key) || '';
  }

  saveRating(week, day, exerciseIndex, rating, element) {
    const key = this.getRatingKey(week, day, exerciseIndex);
    storage.setItem(key, rating);

    // Update UI: remove active class from all buttons in this exercise
    const ratingContainer = element.closest('.difficulty-rating');
    const allButtons = ratingContainer.querySelectorAll('.difficulty-btn');
    allButtons.forEach(btn => btn.classList.remove('active'));

    // Add active class to clicked button
    element.classList.add('active');

    // Show feedback badge
    const feedbackTexts = {
      1: '👍 Facile!',
      2: '💪 Gestibile',
      3: '💯 Giusto',
      4: '🔥 Impegnativo',
      5: '💀 Molto duro'
    };
    this.showBadge(feedbackTexts[rating]);
  }

  loadRating(week, day, exerciseIndex) {
    const key = this.getRatingKey(week, day, exerciseIndex);
    return storage.getItem(key) || null;
  }

  showBadge(text) {
    const badge = document.createElement('div');
    badge.className = 'floating-badge';
    badge.textContent = text;
    document.body.appendChild(badge);

    setTimeout(() => {
      badge.style.opacity = '1';
      badge.style.transform = 'translate(-50%, -50%) scale(1.5)';

      setTimeout(() => {
        badge.style.opacity = '0';
        setTimeout(() => badge.remove(), 500);
      }, 3000);
    }, 100);
  }

  triggerConfetti() {
    const colors = ['#007AFF', '#34C759', '#FF9500', '#FF3B30', '#FFD60A'];

    for (let i = 0; i < 100; i++) {
      setTimeout(() => {
        const confetti = document.createElement('div');
        confetti.className = 'confetti';
        confetti.style.left = Math.random() * 100 + 'vw';
        confetti.style.background = colors[Math.floor(Math.random() * colors.length)];
        confetti.style.animationDuration = (Math.random() * 2 + 2) + 's';
        confetti.style.animationDelay = (Math.random() * 0.5) + 's';
        document.body.appendChild(confetti);

        setTimeout(() => confetti.remove(), 4000);
      }, i * 30);
    }

    if ('vibrate' in navigator) {
      navigator.vibrate([200, 100, 200, 100, 200]);
    }
  }

  updateTraining() {
    const data = dataManager.getData();
    if (!data || !data.training) {
      ui.showLoading('training-content');
      return;
    }

    const weekSelect = document.getElementById('week');
    const daySelect = document.getElementById('day');
    const container = document.getElementById('training-content');

    if (!weekSelect || !daySelect || !container) return;

    const week = weekSelect.value;
    const dayShort = daySelect.value;

    let dayKey = null;
    if (data.training[week]) {
      for (const key of Object.keys(data.training[week])) {
        if (key.startsWith(dayShort)) {
          dayKey = key;
          break;
        }
      }
    }

    if (!dayKey || !data.training[week][dayKey]) {
      container.innerHTML = '<div style="text-align:center;padding:40px;color:var(--text-secondary);">Nessun dato disponibile</div>';
      return;
    }

    const exercises = data.training[week][dayKey];
    let html = `<div class="day-header">📅 ${week} - ${dayShort}</div>`;

    exercises.forEach((ex, index) => {
      const savedNote = this.loadNote(week, dayShort, index);
      const isCompleted = this.isExerciseCompleted(week, dayShort, index);
      const savedRating = this.loadRating(week, dayShort, index);

      html += `
        <div class="exercise-card ${isCompleted ? 'completed' : ''}">
          <div class="exercise-header">
            <div class="exercise-checkbox ${isCompleted ? 'checked' : ''}"
                 onclick="window.toggleCompletion('${week}', '${dayShort}', ${index}, this)">
            </div>
            <div class="exercise-name">💪 ${ex.nome}</div>
          </div>
          <div class="exercise-grid">
            <div class="exercise-stat">
              <div class="stat-label">Serie</div>
              <div class="stat-value">${ex.serie}</div>
            </div>
            <div class="exercise-stat">
              <div class="stat-label">Carico</div>
              <div class="stat-value">${ex.carico}</div>
            </div>
            <div class="exercise-stat">
              <div class="stat-label">%1RM</div>
              <div class="stat-value">${ex.percentuale}</div>
            </div>
            <div class="exercise-stat">
              <div class="stat-label">RPE</div>
              <div class="stat-value">${ex.rpe}</div>
            </div>
          </div>
          ${ex.note && ex.note !== '-' ? `<div class="exercise-note">📌 ${ex.note}</div>` : ''}

          <div class="exercise-difficulty">
            <div class="difficulty-label">Come ti sei sentito? (1=Facile, 5=Molto difficile)</div>
            <div class="difficulty-rating">
              ${[1, 2, 3, 4, 5].map(rating => `
                <button
                  class="difficulty-btn ${savedRating === rating ? 'active' : ''}"
                  onclick="window.saveRating('${week}', '${dayShort}', ${index}, ${rating}, this)"
                  aria-label="Difficoltà ${rating}"
                >
                  ${rating}
                </button>
              `).join('')}
            </div>
          </div>

          <div class="personal-notes">
            <div class="notes-header">
              <div class="notes-label">📝 Note Personali</div>
              <div class="notes-saved">✓ Salvato</div>
            </div>
            <textarea
              class="notes-textarea"
              placeholder="Scrivi qui le tue sensazioni, feedback, variazioni..."
              oninput="window.saveNote('${week}', '${dayShort}', ${index}, this.value)"
            >${savedNote}</textarea>
          </div>
        </div>
      `;
    });

    container.innerHTML = html;
    // Extract day without parentheses for progress bar
    const dayForProgress = dayShort.split(' ')[0];
    this.updateWorkoutProgress(week, dayForProgress);
  }
}

// Initialize and export
const workoutManager = new WorkoutManager();
workoutManager.init();

export { workoutManager };
